import { Inject, Injectable } from '@angular/core';

import { getProperty } from '../../../utils';
import { CYCLE_HANDLING, HttpResourceOptions, HubInfoConfig, HubInfoUrlProvider } from './../../../api';
import {
  WCH_TOKEN_API_URL,
  WCH_TOKEN_BASE_URL,
  WCH_TOKEN_CYCLE_HANDLING_STRATEGY,
  WCH_TOKEN_DELIVERY_URL,
  WCH_TOKEN_FETCH_LEVELS,
  WCH_TOKEN_HTTP_OPTIONS,
  WCH_TOKEN_HTTP_PREVIEW_OPTIONS,
} from './tokens';

/*
 * Base class for services providing hub information. We need a base class to be able to
 * specify  this service as injectable
 */
@Injectable({ providedIn: 'root' })
export class HubInfoService implements HubInfoConfig {
  /**
   * URL to access the API layer
   *
   * Naming of this field according to the field in the rendering context
   *
   * @example 'https://my.digitalexperience.ibm.com/api/345563cf-a83c-40e5-a065-1d6ff36b05c1'
   * @example 'https://my.digitalexperience.ibm.com/api/345563cf-a83c-40e5-a065-1d6ff36b05c1/dxsites/mysite'
   */
  readonly apiUrl?: HubInfoUrlProvider;

  /**
   * URL to access the delivery
   *
   * Naming of this field according to the field in the rendering context
   *
   * @example 'https://my.digitalexperience.ibm.com/345563cf-a83c-40e5-a065-1d6ff36b05c1'
   * @example 'https://my.digitalexperience.ibm.com/345563cf-a83c-40e5-a065-1d6ff36b05c1/dxsites/mysite'
   */
  readonly deliveryUrl?: HubInfoUrlProvider;

  /**
   * URL that represents the base URL of the path based routing of the application. This prefix will be
   * preserved when generating and recognizing URLs. If this property is not configured, then it will be decoded
   * from the window location.
   *
   * @example 'https://my.digitalexperience.ibm.com/345563cf-a83c-40e5-a065-1d6ff36b05c1'
   * @example 'https://my.digitalexperience.ibm.com/345563cf-a83c-40e5-a065-1d6ff36b05c1/dxsites/mysite'
   * @example 'https://my.external.example.com/'
   */
  readonly baseUrl?: HubInfoUrlProvider;

  /**
   * Optionally specify how the SDK makes outbound requests
   */
  readonly httpOptions?: HttpResourceOptions;

  /**
   * Optionally specify how the SDK makes outbound requests for the preview case
   */
  readonly httpPreviewOptions?: HttpResourceOptions;

  /**
   * Optionally specify how the SDK is supposed to deal with
   * cyclic references in the content data structure. Per default
   * the rendering context will break cycles by representing the duplicate
   * element in a reference path by an unresolved reference. When configuring
   * the strategy to {@link CYCLE_HANDLING.RESOLVE}, the {@link ContentrefComponent}
   * will use a resolved refence when rendering the context, instead of the unresolved
   * reference. This bears the risk of an infinite loop during rendering. The
   * actual rendering context objects will still not have cycles, so a JSON serialization
   * of these objects will produce a valid result.
   *
   * Default is {@link CYCLE_HANDLING.BREAK}
   */
  readonly cycleHandlingStrategy?: CYCLE_HANDLING | string;

  /**
   * Number of levels to fetch per request to the rendering context. If missing
   * all levels will be fetched.
   */
  readonly fetchLevels?: number;

  constructor(
    @Inject(WCH_TOKEN_API_URL) aApiUrl: HubInfoUrlProvider,
    @Inject(WCH_TOKEN_DELIVERY_URL) aDeliveryUrl: HubInfoUrlProvider,
    @Inject(WCH_TOKEN_BASE_URL) aBaseUrl: HubInfoUrlProvider,
    @Inject(WCH_TOKEN_HTTP_OPTIONS) aHttpOptions: HttpResourceOptions,
    @Inject(WCH_TOKEN_HTTP_PREVIEW_OPTIONS)
    aHttpPreviewOptions: HttpResourceOptions,
    @Inject(WCH_TOKEN_CYCLE_HANDLING_STRATEGY)
    aCycleHandlingStrategy: CYCLE_HANDLING | string,
    @Inject(WCH_TOKEN_FETCH_LEVELS) aFetchLevels: number
  ) {
    // get the tokens
    this.apiUrl = aApiUrl;
    this.deliveryUrl = aDeliveryUrl;
    this.baseUrl = aBaseUrl;
    this.httpOptions = aHttpOptions;
    this.httpPreviewOptions = aHttpPreviewOptions;
    this.cycleHandlingStrategy = aCycleHandlingStrategy;
    this.fetchLevels = aFetchLevels;
  }
}

export function selectApiURL(aService?: HubInfoConfig): HubInfoUrlProvider {
  return getProperty(aService, 'apiUrl');
}

export function selectDeliveryURL(
  aService?: HubInfoConfig
): HubInfoUrlProvider {
  return getProperty(aService, 'deliveryUrl');
}

export function selectBaseURL(aService?: HubInfoConfig): HubInfoUrlProvider {
  return getProperty(aService, 'baseUrl');
}

export function selectHttpOptions(
  aService?: HubInfoConfig
): HttpResourceOptions {
  return getProperty(aService, 'httpOptions');
}

export function selectHttpPreviewOptions(
  aService?: HubInfoConfig
): HttpResourceOptions {
  return getProperty(aService, 'httpPreviewOptions');
}

export function selectCycleHandlingStrategy(
  aService?: HubInfoConfig
): CYCLE_HANDLING | string {
  return getProperty(aService, 'cycleHandlingStrategy');
}

export function selectFetchLevels(aService?: HubInfoConfig): number {
  return getProperty(aService, 'fetchLevels');
}